import { i18n } from "./i18n";
import { resources } from "./resources";
import { AppStorage } from "../../service/UserStorage";

export * from "./i18n";
export * from "./resources";

function collectVietnameseResourceKeys(value: unknown, prefix = ""): Record<string, string> {
  if (!value || typeof value !== "object") {
    return {};
  }

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, string>>((result, [key, child]) => {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    if (typeof child === "string") {
      result[child.trim()] = nextPrefix;
      return result;
    }
    Object.assign(result, collectVietnameseResourceKeys(child, nextPrefix));
    return result;
  }, {});
}

const vietnameseResourceKeys = collectVietnameseResourceKeys(resources.vi.translation);

/**
 * Compatibility adapter for pages that are being migrated incrementally.
 * New code should call the react-i18next `t` function directly.
 */
export function translate(errorCode: string, message: string | null = null): string {
  if (!errorCode) {
    return errorCode;
  }

  if (errorCode.includes("+")) {
    return errorCode
      .split("+")
      .map((item) => translate(item, message))
      .join(" ")
      .trim();
  }

  const normalizedErrorCode = errorCode.trim();
  const key = vietnameseResourceKeys[normalizedErrorCode]
    || normalizedErrorCode;
  const translated = i18n.t(key);
  return translated !== key ? translated : message || errorCode;
}

function firstString(...values: unknown[]): string | undefined {
  return values.find((value): value is string => typeof value === "string" && value.trim().length > 0);
}

function getApplicationLocalization(): Record<string, string> | undefined {
  const rawLocalization = AppStorage.get("localization");
  if (!rawLocalization) {
    return undefined;
  }

  try {
    const localization = JSON.parse(rawLocalization);
    return localization && typeof localization === "object" ? localization : undefined;
  } catch {
    return undefined;
  }
}

function resolveApplicationLocalizedError(errorKey: string, fallbackMessage?: string): string | undefined {
  const localizedMessage = getApplicationLocalization()?.[errorKey];
  return typeof localizedMessage === "string" && localizedMessage.trim()
    ? localizedMessage
    : fallbackMessage;
}

/**
 * Extracts backend-provided messages without translating them on the client.
 * Only the final fallback is owned by the frontend and therefore translatable.
 */
export function translateError(response: any): string {
  const payload = response?.response || response;

  if (typeof payload === "string") {
    return resolveApplicationLocalizedError(payload, payload) || payload;
  }

  if (!payload || typeof payload !== "object") {
    return i18n.t("error.generic");
  }

  const errorPayload = payload?.data?.error || payload?.error;
  if (typeof errorPayload === "string" && errorPayload.trim()) {
    return resolveApplicationLocalizedError(errorPayload, errorPayload) || errorPayload;
  }
  const errorMessage = firstString(errorPayload?.message);

  if (String(errorPayload?.code) === "9999" && errorMessage) {
    return errorMessage;
  }

  const validationErrors = errorPayload?.validationErrors || payload?.data?.validationErrors || payload?.validationErrors;
  if (Array.isArray(validationErrors)) {
    const validationMessage = validationErrors
      .map((item) => item?.message)
      .filter((message): message is string => typeof message === "string" && message.trim().length > 0)
      .join(", ");
    if (validationMessage) {
      return validationMessage;
    }
  }

  if (errorPayload?.code !== undefined && errorPayload?.code !== null && errorPayload?.code !== "") {
    const errorKey = `ErrorCode-${errorPayload.code}`;
    const localizedMessage = resolveApplicationLocalizedError(errorKey, errorMessage);
    if (localizedMessage) {
      return localizedMessage;
    }
  }

  if (errorMessage) {
    return errorMessage;
  }

  const backendMessage = firstString(
    payload?.data?.message,
    payload?.message,
    payload?.statusText,
  );
  return backendMessage || i18n.t("error.generic");
}

export type LocalizedText =
  | string
  | { key: string; values?: Record<string, unknown> }
  | { text: string };

export function localized(key: string, values?: Record<string, unknown>): LocalizedText {
  return { key, values };
}

/**
 * Localize frontend-owned option metadata without touching labels supplied by
 * an API.  Static option lists can carry `translationKey`; API option lists
 * omit it and therefore pass through unchanged.
 */
export function localizeOptions<T extends Record<string, any>>(options: T[] | undefined): T[] {
  return (options || []).map((option) => {
    if (!option?.translationKey) {
      return option;
    }
    return {
      ...option,
      label: i18n.t(option.translationKey),
    };
  });
}

export function resolveLocalizedText(value: LocalizedText | null | undefined): string {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if ("text" in value) {
    return value.text;
  }
  return i18n.t(value.key, value.values);
}
